import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import gql from 'graphql-tag';
import { Dropdown, Form } from 'react-bootstrap';
import { Button, Spinner } from '@storable/canaveral-components';
import useCurrentPage from '../../../hooks/useCurrentPage';
import Address from '../../../components/stateless/Address';
import FacilityName from '../../../components/stateless/FacilityName';
import { pushRoute } from '../../../../modules/routing';
import styles from './styles.scss';
import Icon from '../../../../modules/react/components/stateless/Icon';
import useFacilityManager from './useFacilityManager';

const propTypes = {
  user: PropTypes.shape({
    latitude: PropTypes.string,
    longitude: PropTypes.string,
  }).isRequired,
  allFacilities: PropTypes.arrayOf(PropTypes.shape({
    displayName: PropTypes.string,
    address: PropTypes.shape({
      address1: PropTypes.string,
      address2: PropTypes.string,
      city: PropTypes.string,
      country: PropTypes.string,
      fullAddress: PropTypes.string,
      latitude: PropTypes.string,
      longitude: PropTypes.string,
      postal: PropTypes.string,
      state: PropTypes.string,
    }),
  })),
  isEditable: PropTypes.bool.isRequired,
  loadingFacilities: PropTypes.bool,
};

const defaultProps = {
  allFacilities: [],
  loadingFacilities: undefined,
};

const FacilitySearchBar = ({
  isEditable,
  user,
  allFacilities,
  loadingFacilities,
  setFocused,
}) => {
  const [showList, setShowList] = useState(false);
  const [showAllFacilities, setShowAllFacilities] = useState(false);
  const [showAllFilteredFacilities, setShowAllFilteredFacilities] = useState(false);
  const nodeRef = useRef(null);

  const {
    primaryColorClass,
    secondaryColorBackgroundClass,
    primaryColorBackgroundHoverClass,
    buttonStyleClass,
  } = useCurrentPage();

  const zipCodeRadius = 50;

  const {
    facilitiesNearZipCode,
    facilitiesNearLocation,
    filteredFacilities,
    setSearchText,
    searchText,
    isZipCodeSearch,
    isLocationEnabled,
  } = useFacilityManager(allFacilities, zipCodeRadius);

  const handleDocumentClick = (event) => {
    const node = nodeRef.current;
    if (!node || node.contains(event.target)) {
      if (searchText > 1) {
        setShowList(true);
      }
      return;
    }
    setShowList(false);
  };

  const handleDocumentKeyDown = ({ keyCode }) => {
    if (keyCode === 27 && showList) {
      setShowList(false);
      setSearchText('');
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleDocumentClick, { passive: true });
    document.addEventListener('keydown', handleDocumentKeyDown, { passive: true });
    return () => {
      document.removeEventListener('click', handleDocumentClick);
      document.removeEventListener('keydown', handleDocumentKeyDown);
    };
  }, [user, allFacilities]);

  const handleSearch = ({ target: { value } }) => {
    if (value.length > 1) {
      setShowList(true);
      if (showAllFacilities) {
        setShowAllFacilities(false);
      }
      if (showAllFilteredFacilities) {
        setShowAllFilteredFacilities(false);
      }
    } else {
      setShowList(false);
    }
    setSearchText(value);
  };

  const handlePageClick = ({ id, path }) => {
    pushRoute(isEditable ? `/admin/page/edit/${id}` : path);
    setShowList(false);
  };

  const handleShowMoreClick = showFiltered => (event) => {
    event.nativeEvent.stopImmediatePropagation();
    if (showFiltered) {
      setShowAllFilteredFacilities(true);
    } else {
      setShowAllFacilities(true);
    }
  };

  const handleSearchKeyDown = (event) => {
    if (event.nativeEvent.keyCode === 13) {
      handleSearch(event);
    }
  };

  const handleCloseSearch = () => {
    setShowList(false);
    setSearchText('');
    setShowAllFilteredFacilities(false);
    setShowAllFacilities(false);
  };

  const renderFacilityItem = ({ address, distance, page, displayName, settings }, index) => (
    /* eslint-disable jsx-a11y/click-events-have-key-events */
    /* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
    <li
      key={index}
      onClick={() => handlePageClick(page)} // Wrap the function call
      className={cx('menu-item', styles.facilityItem)}
    >
      {/* eslint-enable jsx-a11y/click-events-have-key-events */}
      {/* eslint-enable jsx-a11y/no-noninteractive-element-interactions */}
      <div className={cx('pull-left', styles.location)}>
        <span className={cx('icon', 'icon-locations-pin', styles.mapPin, primaryColorClass)} />
        {distance ? (<div>{distance} mi</div>) : null}
        {(distance === 0) && (<div>{'< 1'} mi</div>)}
      </div>
      <div className={styles.pageName}>
        <FacilityName displayName={displayName} className={styles.facilityName} />
        <Address className={styles.address} {...address} />
        {settings && settings.tagline ? settings.tagline : null}
      </div>
    </li>
  );

  const renderShowMoreButton = showFiltered => (
    /* eslint-disable jsx-a11y/click-events-have-key-events */
    /* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
    <li
      className={cx(
        secondaryColorBackgroundClass,
        primaryColorBackgroundHoverClass,
        buttonStyleClass,
        'menu-item',
        styles.showMore,
      )}
      onClick={handleShowMoreClick(showFiltered)}
    >
      {/* eslint-enable jsx-a11y/click-events-have-key-events */}
      {/* eslint-enable jsx-a11y/no-noninteractive-element-interactions */}
      {showFiltered ? 'View more results...' : 'View more facilities...'}
    </li>
  );

  const renderMenuHeader = (menuType) => {
    switch (menuType) {
      case 'exactMatch':
        return 'Search results - exact match';
      case 'nearby':
        return 'See facilities nearest you';
      case 'nearbyZipCode':
        return `Search results within ${zipCodeRadius} miles of ${searchText}`;
      default:
        return 'Search results';
    }
  };

  const renderFacilityList = (facilities, moreAvailable, showFiltered, menuType) => (
    <div>
      <Dropdown.Item header className={styles.header}>
        {renderMenuHeader(menuType)}
      </Dropdown.Item>
      {facilities.map(renderFacilityItem)}
      {moreAvailable ? renderShowMoreButton(showFiltered) : null}
    </div>
  );

  const noResultsFound = <li className={cx(secondaryColorBackgroundClass, 'menu-item', styles.noResultsFound)}>No results found</li>;

  const renderMenu = (large) => {
    const visibilityClass = large ? 'd-none d-sm-block' : 'd-block d-sm-none';

    if (!showList) {
      return null;
    }

    const maxFacilities = large ? 5 : 3;

    let facilitiesNearLocationToBeShown = facilitiesNearLocation.slice(0, 10);
    let filteredFacilitiesToBeShown = filteredFacilities.slice(0, 10);
    // Don't show all facilities if all are already shown in results
    const showAllFacilitiesList = facilitiesNearLocation.length > filteredFacilities.length;
    const moreFacilitiesNearLocationAvailable = facilitiesNearLocation.length > maxFacilities
    && !showAllFacilities;
    if (moreFacilitiesNearLocationAvailable) {
      facilitiesNearLocationToBeShown = facilitiesNearLocationToBeShown.slice(0, maxFacilities);
    }
    const moreFilteredFacilitiesAvailable = filteredFacilitiesToBeShown.length > maxFacilities
    && !showAllFilteredFacilities;
    if (moreFilteredFacilitiesAvailable) {
      filteredFacilitiesToBeShown = filteredFacilitiesToBeShown.slice(0, maxFacilities);
    }

    const facilitiesNearZipCodeToBeShown = facilitiesNearZipCode.slice(0, 5);

    return (
      <ul className={cx('dropdown-menu', visibilityClass, styles.dropdownMenu)}>
        {renderFacilityList(filteredFacilitiesToBeShown, moreFilteredFacilitiesAvailable, true, 'exactMatch')}
        {(!filteredFacilitiesToBeShown || filteredFacilitiesToBeShown.length === 0)
          && noResultsFound}
        {isZipCodeSearch
          && renderFacilityList(facilitiesNearZipCodeToBeShown, false, true, 'nearbyZipCode')}
          {isZipCodeSearch
          && (!facilitiesNearZipCodeToBeShown || facilitiesNearZipCodeToBeShown.length === 0)
          && noResultsFound}
        {isLocationEnabled && showAllFacilitiesList
         && renderFacilityList(facilitiesNearLocationToBeShown,
           moreFacilitiesNearLocationAvailable, false, 'nearby')}
      </ul>
    );
  };

  return (
    <div className={cx(styles.facilityDropdown, 'dropdown', showList ? 'open' : '')} ref={nodeRef}>
          <Form.Control
            type="text"
            onChange={handleSearch}
            onKeyDown={handleSearchKeyDown}
            onFocus={setFocused}
            placeholder="Enter City, State Abbreviation, or Zip"
            value={searchText}
            data-testid="facility-search-input"
          />
      {loadingFacilities && (
        <div className={styles.searchButton}>
          <Spinner />
        </div>
      )}
      {!loadingFacilities && (showList ? (
        <Button
          context="bare"
          label="close"
          className={styles.exitIcon}
          onClick={handleCloseSearch}
        >
          <Icon id="exit" label="exitIcon" />
        </Button>
      ) : (
        <Button
          context="bare"
          label="search"
          className={cx(primaryColorClass, styles.searchButton)}
          onClick={handleSearch}
          value={searchText}
        >
          <Icon id="search" label="searchIcon" />
        </Button>
      ))}
          {renderMenu(true)}
      {renderMenu(false)}
    </div>
  );
};

FacilitySearchBar.propTypes = propTypes;
FacilitySearchBar.defaultProps = defaultProps;

FacilitySearchBar.fragments = {
  facility: gql`
    fragment FacilitySearchBarFacility on Facility {
      page(locale: $locale) {
        id
        path
      }
      address {
        ...FacilityAddress
      }
      ...FacilityNameFacility
    }

    ${FacilityName.fragments.facility}
    ${Address.fragments.address}
  `,
};

export default FacilitySearchBar;
